import type {
  ApiRequestBody,
  ApiResponseBody,
  ApiSearchQuery,
} from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetEquipmentListQuery = ApiSearchQuery<'/v2/equipment', 'get'>;
export type GetEquipmentListResponse = ApiResponseBody<'/v2/equipment', 'get'>;
export type EquipmentListItem = GetEquipmentListResponse['items'][0];

export type EquipmentOrderBy = NonNullable<GetEquipmentListQuery['orderBy']>;
export const EquipmentOrderBy = {
  NAME: 'NAME',
  EQUIPMENT_TYPE: 'EQUIPMENT_TYPE',
  MODEL: 'MODEL',
  SERIAL_NUMBER: 'SERIAL_NUMBER',
  ASSET_TAG: 'ASSET_TAG',
  CREATED_AT: 'CREATED_AT',
  ASSIGNMENT_START_DATE: 'ASSIGNMENT_START_DATE',
  EXPECTED_RETURN_DATE: 'EXPECTED_RETURN_DATE',
  PURCHASE_DATE: 'PURCHASE_DATE',
  PLACED_DATE: 'PLACED_DATE',
  DISPOSAL_DATE: 'DISPOSAL_DATE',
  FAILURE_DATE: 'FAILURE_DATE',
  MANUFACTURER: 'MANUFACTURER',
  PRODUCT_NAME: 'PRODUCT_NAME',
  EQUIPMENT_STATUS: 'EQUIPMENT_STATUS',
  FAILURE_DESCRIPTION: 'FAILURE_DESCRIPTION',
  ASSIGNED_PERSON_NAME: 'ASSIGNED_PERSON_NAME',
  PLACED_LOCATION: 'PLACED_LOCATION',
  ITEM_TYPE: 'ITEM_TYPE',
} as const satisfies Record<EquipmentOrderBy, string>;

export type GetEquipmentCountQuery = ApiSearchQuery<
  '/v2/equipment-count',
  'get'
>;
export type GetEquipmentCountResponse = ApiResponseBody<
  '/v2/equipment-count',
  'get'
>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

export type GetEquipmentResponse = ApiResponseBody<
  '/v2/equipment/{equipmentId}',
  'get'
>;

export type PostEquipmentRequest = ApiRequestBody<'/v2/equipment', 'post'>;

export type PostEquipmentResponse = ApiResponseBody<'/v2/equipment', 'post'>;

export type PatchEquipmentRequest = ApiRequestBody<
  '/v2/equipment/{equipmentId}',
  'patch'
>;
export type PatchEquipmentResponse = ApiResponseBody<
  '/v2/equipment/{equipmentId}',
  'patch'
>;

export type ChangeEquipmentStatusRequest = ApiRequestBody<
  '/v2/equipment/{equipmentId}/status',
  'patch'
>;
export type ChangeEquipmentStatusResponse = ApiResponseBody<
  '/v2/equipment/{equipmentId}/status',
  'patch'
>;

export type GetEquipmentCsvQuery = Omit<
  ApiSearchQuery<'/v2/equipment-csv', 'get'>,
  'mode' | 'encoding'
>;
export type GetEquipmentCsvResponse = ApiResponseBody<
  '/v2/equipment-csv',
  'get'
>;

// ---------------------------------------------------------------------------
// Equipment Status
// ---------------------------------------------------------------------------

export type EquipmentStatus = NonNullable<
  GetEquipmentResponse['equipmentStatus']
>;
export const EquipmentStatus = {
  AVAILABLE: 'AVAILABLE',
  ASSIGNED: 'ASSIGNED',
  PLACED: 'PLACED',
  UNDER_REPAIR: 'UNDER_REPAIR',
  DECOMMISSIONED: 'DECOMMISSIONED',
} as const satisfies Record<EquipmentStatus, string>;

const EQUIPMENT_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentStatus),
);
export function isEquipmentStatus(
  value: string | null | undefined,
): value is EquipmentStatus {
  return value != null && EQUIPMENT_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type EquipmentStatusFilter = EquipmentStatus | 'ALL';

// ---------------------------------------------------------------------------
// Equipment Item Type
// ---------------------------------------------------------------------------

export type PostEquipmentItemRequest = ApiRequestBody<
  '/v2/equipment-items',
  'post'
>;

export type EquipmentItemType = NonNullable<
  PostEquipmentItemRequest['itemType']
>;
export const EquipmentItemType = {
  LOANABLE: 'LOANABLE',
  TRANSFERABLE: 'TRANSFERABLE',
  CONSUMABLE: 'CONSUMABLE',
} as const satisfies Record<EquipmentItemType, string>;

export const EQUIPMENT_ITEM_TYPES = Object.values(EquipmentItemType);

const EQUIPMENT_ITEM_TYPE_VALUES: ReadonlySet<string> = new Set(
  EQUIPMENT_ITEM_TYPES,
);
export function isEquipmentItemType(
  value: string | null | undefined,
): value is EquipmentItemType {
  return value != null && EQUIPMENT_ITEM_TYPE_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment End of Life Type (PR2 用、型定義のみ先行)
// ---------------------------------------------------------------------------

export type EquipmentEndOfLifeType = NonNullable<
  NonNullable<GetEquipmentResponse['lifecycle']>['endOfLifeType']
>;
export const EquipmentEndOfLifeType = {
  DISPOSED: 'DISPOSED',
  SOLD: 'SOLD',
  TRANSFERRED: 'TRANSFERRED',
  LOST: 'LOST',
  INITIAL_DEFECT: 'INITIAL_DEFECT',
  OTHER: 'OTHER',
} as const satisfies Record<EquipmentEndOfLifeType, string>;

const END_OF_LIFE_TYPE_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentEndOfLifeType),
);
export function isEquipmentEndOfLifeType(
  value: string | null | undefined,
): value is EquipmentEndOfLifeType {
  return value != null && END_OF_LIFE_TYPE_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Currency
// ---------------------------------------------------------------------------

export type EquipmentCurrency = NonNullable<
  PostEquipmentRequest['unitPriceCurrency']
>;
export const EquipmentCurrency = {
  JPY: 'JPY',
  USD: 'USD',
  IDR: 'IDR',
  THB: 'THB',
  VND: 'VND',
  CNY: 'CNY',
} as const satisfies Record<EquipmentCurrency, string>;

export const EQUIPMENT_CURRENCIES: EquipmentCurrency[] =
  Object.values(EquipmentCurrency);

const EQUIPMENT_CURRENCY_SET: ReadonlySet<string> = new Set(
  EQUIPMENT_CURRENCIES,
);

export function isEquipmentCurrency(
  value: string | undefined | null,
): value is EquipmentCurrency {
  return value != null && EQUIPMENT_CURRENCY_SET.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Control Type
// ---------------------------------------------------------------------------

export type EquipmentControlType = NonNullable<
  GetEquipmentResponse['controlType']
>;
export const EquipmentControlType = {
  INVENTORY_MANAGEMENT: 'INVENTORY_MANAGEMENT',
  DISTRIBUTION_LOG_MANAGEMENT: 'DISTRIBUTION_LOG_MANAGEMENT',
  UNMANAGED: 'UNMANAGED',
} as const satisfies Record<EquipmentControlType, string>;

const CONTROL_TYPE_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentControlType),
);
export function isEquipmentControlType(
  value: string | null | undefined,
): value is EquipmentControlType {
  return value != null && CONTROL_TYPE_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Usage Status
// ---------------------------------------------------------------------------

export type EquipmentUsageStatus = NonNullable<
  NonNullable<GetEquipmentResponse['assignment']>['usageStatus']
>;
export const EquipmentUsageStatus = {
  IN_USE: 'IN_USE',
  RETURNED: 'RETURNED',
  UNDER_REPAIR: 'UNDER_REPAIR',
  DISPOSED: 'DISPOSED',
  STORED: 'STORED',
  SCHEDULED_FOR_LENDING: 'SCHEDULED_FOR_LENDING',
} as const satisfies Record<EquipmentUsageStatus, string>;

// ---------------------------------------------------------------------------
// Equipment Usage Type
// ---------------------------------------------------------------------------

export type EquipmentUsageType = NonNullable<
  NonNullable<GetEquipmentResponse['assignment']>['usageType']
>;
export const EquipmentUsageType = {
  PERSONAL_ASSIGNMENT: 'PERSONAL_ASSIGNMENT',
  SHARED_USE: 'SHARED_USE',
  SHORT_TERM_LENDING: 'SHORT_TERM_LENDING',
  WORK_FROM_HOME: 'WORK_FROM_HOME',
} as const satisfies Record<EquipmentUsageType, string>;

const USAGE_TYPE_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentUsageType),
);
export function isEquipmentUsageType(
  value: string | null | undefined,
): value is EquipmentUsageType {
  return value != null && USAGE_TYPE_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Placement Type
// ---------------------------------------------------------------------------

export type EquipmentPlacementType = NonNullable<
  NonNullable<GetEquipmentResponse['placement']>['placementType']
>;
export const EquipmentPlacementType = {
  STORED: 'STORED',
  FIXED: 'FIXED',
  FREE: 'FREE',
  MOVING: 'MOVING',
} as const satisfies Record<EquipmentPlacementType, string>;

const PLACEMENT_TYPE_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentPlacementType),
);
export function isEquipmentPlacementType(
  value: string | null | undefined,
): value is EquipmentPlacementType {
  return value != null && PLACEMENT_TYPE_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Placement Status
// ---------------------------------------------------------------------------

export type EquipmentPlacementStatus = NonNullable<
  NonNullable<GetEquipmentResponse['placement']>['placementStatus']
>;
export const EquipmentPlacementStatus = {
  IN_USE: 'IN_USE',
  STORED: 'STORED',
  MOVING: 'MOVING',
  TO_BE_DISPOSED: 'TO_BE_DISPOSED',
  NOT_PLACED: 'NOT_PLACED',
} as const satisfies Record<EquipmentPlacementStatus, string>;

// ---------------------------------------------------------------------------
// Equipment Placement Verification Status
// ---------------------------------------------------------------------------

export type EquipmentPlacementVerificationStatus = NonNullable<
  NonNullable<GetEquipmentResponse['placement']>['placementVerificationStatus']
>;
export const EquipmentPlacementVerificationStatus = {
  OK: 'OK',
  MISMATCH: 'MISMATCH',
  UNVERIFIED: 'UNVERIFIED',
} as const satisfies Record<EquipmentPlacementVerificationStatus, string>;

const VERIFICATION_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentPlacementVerificationStatus),
);
export function isEquipmentPlacementVerificationStatus(
  value: string | null | undefined,
): value is EquipmentPlacementVerificationStatus {
  return value != null && VERIFICATION_STATUS_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Placement Verification Method
// ---------------------------------------------------------------------------

export type EquipmentPlacementVerificationMethod = NonNullable<
  NonNullable<GetEquipmentResponse['placement']>['verificationMethod']
>;
export const EquipmentPlacementVerificationMethod = {
  PHYSICAL_CHECK: 'PHYSICAL_CHECK',
  SCAN: 'SCAN',
  PHOTO_RECORD: 'PHOTO_RECORD',
} as const satisfies Record<EquipmentPlacementVerificationMethod, string>;

const VERIFICATION_METHOD_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentPlacementVerificationMethod),
);
export function isEquipmentPlacementVerificationMethod(
  value: string | null | undefined,
): value is EquipmentPlacementVerificationMethod {
  return value != null && VERIFICATION_METHOD_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Operational Status
// ---------------------------------------------------------------------------

export type EquipmentOperationalStatus = NonNullable<
  NonNullable<
    NonNullable<GetEquipmentResponse['lifecycle']>['operationalStatus']
  >
>;
export const EquipmentOperationalStatus = {
  ACTIVE: 'ACTIVE',
  STOPPED: 'STOPPED',
  ON_LOAN: 'ON_LOAN',
} as const satisfies Record<EquipmentOperationalStatus, string>;

const OPERATIONAL_STATUS_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentOperationalStatus),
);
export function isEquipmentOperationalStatus(
  value: string | null | undefined,
): value is EquipmentOperationalStatus {
  return value != null && OPERATIONAL_STATUS_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Disposal Method
// ---------------------------------------------------------------------------

export type EquipmentDisposalMethod = NonNullable<
  NonNullable<GetEquipmentResponse['lifecycle']>['disposalMethod']
>;
export const EquipmentDisposalMethod = {
  RECYCLING: 'RECYCLING',
  PHYSICAL_DESTRUCTION: 'PHYSICAL_DESTRUCTION',
  VENDOR_REQUEST: 'VENDOR_REQUEST',
} as const satisfies Record<EquipmentDisposalMethod, string>;

const DISPOSAL_METHOD_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentDisposalMethod),
);
export function isEquipmentDisposalMethod(
  value: string | null | undefined,
): value is EquipmentDisposalMethod {
  return value != null && DISPOSAL_METHOD_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Equipment Wipe Method
// ---------------------------------------------------------------------------

export type EquipmentWipeMethod = NonNullable<
  NonNullable<GetEquipmentResponse['lifecycle']>['wipeMethod']
>;
export const EquipmentWipeMethod = {
  PHYSICAL_DESTRUCTION: 'PHYSICAL_DESTRUCTION',
  DOD_STANDARD: 'DOD_STANDARD',
  ENCRYPTION_DELETION: 'ENCRYPTION_DELETION',
} as const satisfies Record<EquipmentWipeMethod, string>;

const WIPE_METHOD_VALUES: ReadonlySet<string> = new Set(
  Object.values(EquipmentWipeMethod),
);
export function isEquipmentWipeMethod(
  value: string | null | undefined,
): value is EquipmentWipeMethod {
  return value != null && WIPE_METHOD_VALUES.has(value);
}
